import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import { themeUsesQuizRounds } from "../../../utils/quizTheme";

function s3AssetUrl(base, filename) {
  if (!base || !filename) return "";
  return base + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

const LANDING_TITLE_MAX_LEN = 15;

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [rules, setRules] = useState([""]);

  const [landingPageTitle, setLandingPageTitle] = useState("");
  const [thankYouPage, setThankYouPage] = useState("");
  const [thankYouGif, setThankYouGif] = useState("");
  const [isUploadingGif, setIsUploadingGif] = useState(false);
  const gifInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
    quiz_question_text_color: "#1a1a1a",
    quiz_question_background_color: "#ffffff",
    quiz_option_text_color: "#0d1b5c",
    quiz_option_background_color: "#eceff1",
    rules_text_color: "#333333",
  });

  useEffect(() => {
    if (!data) return;
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    setColors((prev) => ({
      text_color: (source?.text_color ?? data.text_color) ?? prev.text_color,
      button_color: (source?.button_color ?? data.button_color) ?? prev.button_color,
      button_backgroundcolor:
        (source?.button_backgroundcolor ?? data.button_backgroundcolor) ?? prev.button_backgroundcolor,
      quiz_question_text_color:
        ("quiz_question_text_color" in data
          ? data.quiz_question_text_color
          : prev.quiz_question_text_color) ?? prev.quiz_question_text_color,
      quiz_question_background_color:
        ("quiz_question_background_color" in data
          ? data.quiz_question_background_color
          : prev.quiz_question_background_color) ?? prev.quiz_question_background_color,
      quiz_option_text_color:
        ("quiz_option_text_color" in data ? data.quiz_option_text_color : prev.quiz_option_text_color) ??
        prev.quiz_option_text_color,
      quiz_option_background_color:
        ("quiz_option_background_color" in data
          ? data.quiz_option_background_color
          : prev.quiz_option_background_color) ?? prev.quiz_option_background_color,
      rules_text_color:
        data.rules_text_color ??
        source?.text_color ??
        data.text_color ??
        prev.rules_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    // Only overwrite when the key exists on the payload. If fetchThemeData returns a
    // partial object (missing keys), do not use undefined → "" or we wipe a filename
    // just set by upload and abort the in-flight S3 image (NS_BINDING_ABORTED).
    if ("landing_page_title" in data) {
      const t = String(data.landing_page_title ?? "");
      setLandingPageTitle(t.slice(0, LANDING_TITLE_MAX_LEN));
    }
    if ("custom_text_thank_you_page" in data) {
      setThankYouPage(data.custom_text_thank_you_page ?? "");
    }
    if ("thank_you_page" in data) setThankYouGif(data.thank_you_page ?? "");
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const S3 = process.env.REACT_APP_S3_PATH || "";

  const handleGifUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "gif") {
      Swal.fire("Invalid file", "Only GIF files are allowed.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      return;
    }

    setIsUploadingGif(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", "thank_you_page");
    fd.append("themeName", String(currentTheme));

    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      setThankYouGif(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", "Thank you GIF updated.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setIsUploadingGif(false);
    if (gifInputRef.current) gifInputRef.current.value = "";
  };

  const handleLandingPageTitleChange = (val) => {
    let next = val;
    if (next.length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game page title can be at most ${LANDING_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, LANDING_TITLE_MAX_LEN);
    }
    setLandingPageTitle(next);
  };

  const handleThankYouPageChange = (val) => {
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank you page message can be at most 300 characters. Extra text has been trimmed.",
        "warning"
      );
      val = val.slice(0, 300);
    }
    setThankYouPage(val);
  };

  // --- submit all ---
  const handleSubmit = async () => {
    if (String(landingPageTitle ?? "").length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Invalid title",
        `Game page title must be at most ${LANDING_TITLE_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const trimmedRules = rules.map((r) => String(r ?? "").trim());
    if (trimmedRules.some((r) => r === "")) {
      Swal.fire(
        "Incomplete rules",
        "Please add text to every rule, or remove empty rows using the × button before saving.",
        "warning"
      );
      return;
    }
    if (trimmedRules.length === 0) {
      Swal.fire(
        "Rules required",
        "Please add at least one rule with text before saving.",
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        landing_page_title: String(landingPageTitle ?? "").slice(0, LANDING_TITLE_MAX_LEN),
        custom_text_thank_you_page: thankYouPage,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        quiz_question_text_color: colors.quiz_question_text_color,
        quiz_question_background_color: colors.quiz_question_background_color,
        quiz_option_text_color: colors.quiz_option_text_color,
        quiz_option_background_color: colors.quiz_option_background_color,
        rules_text_color: colors.rules_text_color,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            {/* <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
                </div>
                <div className="form-group-v1">
                  <label>Game page title</label>
                  <textarea
                    value={landingPageTitle}
                    onChange={(e) => handleLandingPageTitleChange(e.target.value)}
                    placeholder="Game page title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={LANDING_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {LANDING_TITLE_MAX_LEN} characters.
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Thank you page message</label>
                  <textarea
                    value={thankYouPage}
                    onChange={(e) => handleThankYouPageChange(e.target.value)}
                    placeholder="Thank you page message"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={300}
                  />
                  <div className="code">* Maximum 300 characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Thank You GIF </label>
                  {thankYouGif && (
                    <div className="thankyou-gif-preview">
                      <img
                        src={s3AssetUrl(S3, thankYouGif)}
                        alt="Thank you"
                        className="thankyou-gif-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={gifInputRef}
                    type="file"
                    accept=".gif"
                    onChange={handleGifUpload}
                    disabled={isUploadingGif}
                    className="thankyou-gif-input"
                  />
                  {isUploadingGif && (
                    <div className="code">Uploading…</div>
                  )}
                  <div className="code">* Accepts GIF only. Max 5 MB.</div>
                </div>
              </div>
            </div> */}

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                  {data && themeUsesQuizRounds(data) && (
                    <>
                      <div className="pallet-row">
                        <div className="pallet-input">
                          <div className="color-holder">
                            <input
                              type="color"
                              className="pallet-swatch"
                              value={colors.quiz_question_text_color}
                              onChange={handleColorChange("quiz_question_text_color")}
                            />
                            <label>Question text color</label>
                          </div>
                          <div className="color-code">{colors.quiz_question_text_color}</div>
                        </div>
                      </div>
                      <div className="pallet-row">
                        <div className="pallet-input">
                          <div className="color-holder">
                            <input
                              type="color"
                              className="pallet-swatch"
                              value={colors.quiz_question_background_color}
                              onChange={handleColorChange("quiz_question_background_color")}
                            />
                            <label>Question background color</label>
                          </div>
                          <div className="color-code">{colors.quiz_question_background_color}</div>
                        </div>
                      </div>
                      <div className="pallet-row">
                        <div className="pallet-input">
                          <div className="color-holder">
                            <input
                              type="color"
                              className="pallet-swatch"
                              value={colors.quiz_option_text_color}
                              onChange={handleColorChange("quiz_option_text_color")}
                            />
                            <label>Quiz answer text</label>
                          </div>
                          <div className="color-code">{colors.quiz_option_text_color}</div>
                        </div>
                      </div>
                      <div className="pallet-row">
                        <div className="pallet-input">
                          <div className="color-holder">
                            <input
                              type="color"
                              className="pallet-swatch"
                              value={colors.quiz_option_background_color}
                              onChange={handleColorChange("quiz_option_background_color")}
                            />
                            <label>Quiz answer background</label>
                          </div>
                          <div className="color-code">{colors.quiz_option_background_color}</div>
                        </div>
                      </div>
                      <div className="pallet-row">
                        <div className="pallet-input">
                          <div className="color-holder">
                            <input
                              type="color"
                              className="pallet-swatch"
                              value={colors.rules_text_color}
                              onChange={handleColorChange("rules_text_color")}
                            />
                            <label>Rules text color</label>
                          </div>
                          <div className="color-code">{colors.rules_text_color}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
